/**
 * StatusSelect Component
 *
 * Dropdown for selecting task status with icons.
 */

import { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronDown,
  CircleDashed,
  Zap,
  ShieldAlert,
  Sparkles,
  Archive,
  Clock,
  Flag,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';
import { useDropdownPosition } from '@/hooks/useDropdownPosition';
import { useClickOutside } from '@/hooks/useClickOutside';
import { TASK_STATUS, normalizeStatus } from '@/constants/taskStatuses';
import type { TaskStatusDef } from '@/db/taskStatuses';

const ICON_MAP: Record<string, LucideIcon> = {
  CircleDashed, Zap, ShieldAlert, Sparkles, Archive, Clock, Flag,
};

function resolveColorClass(color?: string | null): string {
  if (!color) return 'text-text-quaternary';
  if (color.startsWith('text-')) return color;
  if (color.startsWith('accent-')) return `text-${color}`;
  return 'text-text-quaternary';
}

const defaultStatusOptions: {
  value: string;
  label: string;
  icon: LucideIcon;
  color: string;
}[] = [
  { value: TASK_STATUS.OPEN, label: 'Aperto', icon: CircleDashed, color: 'text-text-quaternary' },
  { value: TASK_STATUS.IN_PROGRESS, label: 'In corso', icon: Zap, color: 'text-accent-blue' },
  { value: TASK_STATUS.BLOCKED, label: 'Bloccato', icon: ShieldAlert, color: 'text-accent-rose' },
  { value: TASK_STATUS.COMPLETED, label: 'Completato', icon: Sparkles, color: 'text-accent-emerald' },
  { value: TASK_STATUS.CANCELLED, label: 'Annullato', icon: Archive, color: 'text-accent-violet' },
];

export interface StatusSelectProps {
  value: string;
  onChange: (status: string) => void;
  size?: 'sm' | 'md';
  showLabel?: boolean;
  disabled?: boolean;
  customStatuses?: TaskStatusDef[];
}

export function StatusSelect({
  value,
  onChange,
  size = 'md',
  showLabel = true,
  disabled = false,
  customStatuses,
}: StatusSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownStyle = useDropdownPosition(isOpen, triggerRef, {
    offset: 6,
    minWidth: 160,
    matchTriggerWidth: showLabel,
  });

  // Build options from custom statuses if provided
  const statusOptions = customStatuses && customStatuses.length > 0
    ? customStatuses.map(s => ({
        value: s.value,
        label: s.name,
        icon: (ICON_MAP[s.icon || ''] || CircleDashed) as LucideIcon,
        color: resolveColorClass(s.color),
      }))
    : defaultStatusOptions;

  const normalizedValue = normalizeStatus(value);
  const currentStatus =
    statusOptions.find((s) => s.value === normalizedValue) ||
    statusOptions.find((s) => s.value === value) ||
    statusOptions[0];
  const IconComponent = currentStatus.icon;

  useClickOutside([dropdownRef, triggerRef], () => setIsOpen(false), isOpen);

  const handleSelect = useCallback(
    (status: string) => {
      onChange(status);
      setIsOpen(false);
    },
    [onChange]
  );

  return (
    <div ref={triggerRef} className="relative inline-block">
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setIsOpen(!isOpen);
        }}
        disabled={disabled}
        className={clsx(
          'flex items-center gap-1 rounded-md transition-all whitespace-nowrap bg-surface-tertiary',
          size === 'sm' ? 'px-1.5 py-1 h-6' : 'px-2 py-1.5 h-7',
          !disabled && 'hover:bg-surface-hover cursor-pointer',
          disabled && 'cursor-default opacity-70'
        )}
        title={disabled ? undefined : 'Cambia stato'}
      >
        <IconComponent
          className={clsx(size === 'sm' ? 'w-3 h-3' : 'w-4 h-4', 'shrink-0', currentStatus.color)}
        />
        {showLabel && (
          <>
            <span
              className={clsx(
                'font-medium capitalize truncate',
                size === 'sm' ? 'text-[10px]' : 'text-[11px]',
                currentStatus.color
              )}
            >
              {currentStatus.label}
            </span>
            {!disabled && (
              <ChevronDown
                className={clsx(
                  size === 'sm' ? 'w-2.5 h-2.5' : 'w-3 h-3',
                  'shrink-0 text-text-quaternary'
                )}
              />
            )}
          </>
        )}
      </button>

      {isOpen &&
        dropdownStyle &&
        createPortal(
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="py-1 rounded-lg shadow-2xl min-w-[140px] animate-fade-in border border-border-primary/70 bg-surface-primary"
          >
            {statusOptions.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.value}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(option.value);
                  }}
                  className={clsx(
                    'w-full flex items-center gap-2 px-3 py-1.5 text-left text-[11px] hover:bg-white/10 transition-colors',
                    option.value === normalizeStatus(value) && 'bg-white/10'
                  )}
                >
                  <Icon className={clsx('w-3.5 h-3.5', option.color)} />
                  <span className="text-text-primary">{option.label}</span>
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
}
